import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { sincronizarBarraDeStatus } from '@/lib/nativo';

export type Theme = 'light' | 'dark' | 'system';

interface UIState {
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;
  theme: Theme;
  openSidebar: () => void;
  closeSidebar: () => void;
  toggleSidebar: () => void;
  toggleCollapsed: () => void;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const resolveSystemTheme = (): 'light' | 'dark' =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';

export const applyTheme = (theme: Theme) => {
  if (typeof document === 'undefined') return;
  const resolved = theme === 'system' ? resolveSystemTheme() : theme;
  document.documentElement.classList.toggle('dark', resolved === 'dark');
  // No app nativo, a barra de status acompanha o tema.
  void sincronizarBarraDeStatus(resolved === 'dark');
};

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      sidebarOpen: false,
      sidebarCollapsed: false,
      theme: 'system',
      openSidebar: () => set({ sidebarOpen: true }),
      closeSidebar: () => set({ sidebarOpen: false }),
      toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
      toggleCollapsed: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setTheme: (theme) => {
        set({ theme });
        applyTheme(theme);
      },
      toggleTheme: () => {
        const current = get().theme;
        const resolved = current === 'system' ? resolveSystemTheme() : current;
        const next: Theme = resolved === 'dark' ? 'light' : 'dark';
        set({ theme: next });
        applyTheme(next);
      },
    }),
    {
      name: 'ui-store',
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        theme: state.theme,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) applyTheme(state.theme);
      },
    }
  )
);

// Live-update when system theme changes (only relevant for 'system')
if (typeof window !== 'undefined') {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    const { theme } = useUIStore.getState();
    if (theme === 'system') applyTheme('system');
  });
}
